import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation('common');

  const isHomePage = location.pathname === '/' || location.pathname === '';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/testimonials', label: t('nav.testimonials') },
    { path: '/info', label: t('nav.info') },
  ];

  const languages = [
    { code: 'en', label: 'EN', flag: '🇬🇧', name: 'English' },
    { code: 'ru', label: 'RU', flag: '🇷🇺', name: 'Русский' },
    { code: 'no', label: 'NO', flag: '🇳🇴', name: 'Norsk' },
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsLangDropdownOpen(false);
  };

  const textColor = isHomePage && !isScrolled ? 'text-white' : 'text-gray-800';
  const borderColor = isHomePage && !isScrolled ? 'border-white/30' : 'border-[#8B9D83]';
  const hoverBg = isHomePage && !isScrolled ? 'hover:bg-white/10' : 'hover:bg-[#8B9D83]/10';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHomePage ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link
            to="/"
            className={`text-lg sm:text-xl font-light tracking-wider transition-colors ${textColor} cursor-pointer`}
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            aria-label={t('nav.logoAria')}
          >
            {t('nav.logo')}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-light tracking-wide transition-colors hover:text-[#8B9D83] ${
                  location.pathname === link.path
                    ? 'text-[#8B9D83] font-normal'
                    : textColor
                } cursor-pointer`}
              >
                {link.label}
              </Link>
            ))}

            {/* Desktop Language Switcher */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                className={`flex items-center gap-2 px-3 py-1.5 border ${borderColor} rounded-full text-sm font-light transition-all ${hoverBg} ${textColor} cursor-pointer whitespace-nowrap`}
                aria-label={t('nav.changeLanguage')}
              >
                <span>{currentLanguage.flag}</span>
                <span>{currentLanguage.label}</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform ${isLangDropdownOpen ? 'rotate-180' : ''}`} aria-hidden="true"></i>
              </button>

              {isLangDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsLangDropdownOpen(false)}
                  ></div>
                  <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full flex items-center gap-3 px-4 py-2 text-sm font-light transition-colors hover:bg-[#8B9D83]/10 cursor-pointer ${
                          i18n.language === lang.code ? 'bg-[#8B9D83]/5 text-[#8B9D83]' : 'text-gray-700'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <Link
              to="/payment"
              className="px-6 py-2.5 bg-[#8B9D83] text-white text-sm font-light tracking-wide rounded-sm hover:bg-[#7A8C72] transition-all whitespace-nowrap cursor-pointer"
              aria-label={t('nav.bookConsultationAria')}
            >
              {t('nav.bookConsultation')}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center ${textColor} cursor-pointer`}
            aria-label={isMobileMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-5 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block text-base font-light transition-colors hover:text-[#8B9D83] ${
                  location.pathname === link.path ? 'text-[#8B9D83] font-normal' : 'text-gray-800'
                } cursor-pointer`}
              >
                {link.label}
              </Link>
            ))}

            {/* Mobile Language Switcher */}
            <div className="pt-4 border-t border-gray-200">
              <p className="text-xs font-light text-gray-500 mb-3 uppercase tracking-wider">{t('nav.language')}</p>
              <div className="flex gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => changeLanguage(lang.code)}
                    className={`flex items-center gap-2 px-4 py-2 border rounded-full text-sm font-light transition-all cursor-pointer whitespace-nowrap ${
                      i18n.language === lang.code
                        ? 'bg-[#8B9D83] text-white border-[#8B9D83]'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-[#8B9D83]'
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <Link
              to="/payment"
              className="block text-center px-6 py-3 bg-[#8B9D83] text-white text-sm font-light tracking-wide rounded-sm hover:bg-[#7A8C72] transition-all mt-6 whitespace-nowrap cursor-pointer"
              aria-label={t('nav.bookConsultationAria')}
            >
              {t('nav.bookConsultation')}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}